import { AnyAction, createNextState } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/electron';
import { Integrations as TracingIntegrations } from '@sentry/tracing';
import isString from 'lodash/isString';
import pickBy from 'lodash/pickBy';
import createSentryMiddleware from 'redux-sentry-middleware';

import { isDevelopmentEnvironment } from '~/main/environments';
import { PRINTER_CONNECTIONS } from '~/main/printer/types';
import { scrubChannelName } from '~/main/utils/scrubbers';
import { isBrowserCFD, isRenderProcess } from '~/renderer/utils';
import { isSentryReportingEnabled } from '~/root/tools/utils/Sentry';

import type * as SentryTypes from '@sentry/electron';
import type { AppState } from '~/renderer/store/types';

const { init, showReportDialog, setTag } = Sentry;

export const initSentry = (options?: SentryTypes.ElectronOptions) => {
  if (!isSentryReportingEnabled) return;
  let isFirstTrace = true;
  init({
    ...{
      dsn: process.env.SENTRY_DSN,
      release: process.env.HUB_VERSION,
      environment: process.env.NODE_ENV,
      integrations: (isRenderProcess
        ? [new TracingIntegrations.BrowserTracing()]
        : []) as unknown as SentryTypes.ElectronOptions['integrations'],
      tracesSampler: () => {
        // 100% chance of sending the first trace, 1% after that
        if (isDevelopmentEnvironment() || isFirstTrace) {
          isFirstTrace = false;
          return 1;
        }
        return 0.01;
      },
      normalizeDepth: 5,
      beforeSend(event, _hint) {
        if (event.exception) {
          // Note that this only will work in the renderer process, it's a noop on the main process
          showReportDialog();
        }

        return event;
      },
    },
    ...options,
  });
};

const actionTransformer = (action?: AnyAction): SentryTypes.Breadcrumb['data'] | null => {
  if (!action) return;
  return isString(action.payload) ? action : action.payload;
};

export const sentryMiddleware =
  isSentryReportingEnabled && !isBrowserCFD
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      createSentryMiddleware(Sentry as any, {
        getUserContext: (state: AppState) => ({
          id: state.cfd.options.accountId || undefined,
        }),
        getTags: state => {
          const tags: { [key: string]: string } = {};
          tags['printer.receipt.connection'] =
            state.printers.default.RECEIPT?.connection.toString() || '';
          tags['printer.receipt.model'] = state.printers.default.RECEIPT?.model?.toString() || '';
          tags['printer.receipt.firmware'] =
            state.printers.default.RECEIPT?.firmware?.toString() ||
            (state.printers.default.RECEIPT?.connection === PRINTER_CONNECTIONS.USB &&
              state.printers.default.RECEIPT?.meta.isVendorDrivers)
              ? 'vendor drivers'
              : '';
          tags['printer.label.connection'] =
            state.printers.default.LABEL?.connection.toString() || '';
          tags['printer.label.model'] = state.printers.default.LABEL?.model?.toString() || '';
          tags['printer.label.firmware'] = state.printers.default.LABEL?.firmware?.toString() || '';
          const definedTags = pickBy(tags, value => value !== undefined && value !== '');
          return definedTags;
        },
        breadcrumbDataFromAction: actionTransformer,
        actionTransformer,
        stateTransformer: createNextState((newState: AppState) => {
          delete newState.hub.logo;
        }),
      })
    : undefined;

export const setIsDevModeTag = (isDevMode = false) => setTag('isDevMode', String(isDevMode));

export const initSentryScrubbers = (getChannelName: () => string | null) => {
  Sentry.configureScope(scope =>
    scope.addEventProcessor(event => scrubChannelName(event, getChannelName())),
  );
};
